import { React, useCallback, useEffect, useState } from "react";
import {
    TextField,
    Box,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    Chip,
    Checkbox,
    ListItemText,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

import { verifyDomain } from "../../Actions/OrganizationAction";
import { MyAccountBalanceIcon, MyCreditCardIcon, MyVerifiedIcon } from "../../Assets/svg/MyIcons";
import { CustomSwitch } from "../../Assets/customTheme/customswitch";

import { listAllKycPlansDropDownAction } from "../../Actions/KycPlanManagemantAction";
import { stepperTextFieldSx } from "../Tenant/stepperFormStyles";
import "./KycInfoStep.css";

const baseDomain = "netcluesdemo.com";
const KycInfoStep = ({
    tenantId = null,
    formik,
    countryList,
    tenantCurrencies = [],
    qbotDomain,
    paycentralDomain,
    isTrialPortalCreation
}) => {

    const [isEditing, setIsEditing] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [buttonColor, setButtonColor] = useState("");
    const [planList, setPlanList] = useState([]);
    const [previousDomain, setPreviousDomain] = useState("");
    const [isManuallyEdited, setIsManuallyEdited] = useState(false);
    const [responseMessage, setResponseMessage] = useState("");

    const generateDefaultDomainUrl = (subdomain) => {
        if (!subdomain) return "";

        const cleanSubdomain = subdomain
            .replace(/\s+/g, "")
            .replace(/[^a-zA-Z0-9]/g, "")
            .toLowerCase();

        return `${cleanSubdomain}.${baseDomain}`;
    };

    const cleanDomain = (value) =>
        value?.trim().toLowerCase().replace(/^https?:\/\//, "");

    const fetchAllPlansDropDownList = useCallback(async () => {
        try {
            const response = await listAllKycPlansDropDownAction();

            const plans = response?.data?.data || [];

            setPlanList(plans);
            const selectedPlan = plans.find(p => p.isSelected);

            if (selectedPlan) {
                formik.setFieldValue("kycPlanId", selectedPlan.id);
                formik.setFieldValue(
                    "kycBillingCycle",
                    selectedPlan.selectedBillingCycle || ""
                );
            }

        } catch (error) {
            console.error("error ->", error);
        }
    }, [formik]);

    useEffect(() => {
        fetchAllPlansDropDownList();
    }, []);

    const handleEditClick = () => {
        setPreviousDomain(formik.values.kycDomainUrl);

        const currentDomain = formik.values.kycDomainUrl;
        const subdomain = currentDomain
            ?.replace(/^https?:\/\//i, "")
            ?.split(".")[0];

        formik.setFieldValue("kycDomainUrl", subdomain || "");
        formik.setFieldValue("kycIsVerified", false);

        setIsEditing(true);
        setButtonColor("");
        setIsButtonDisabled(true);
    };

    const handleSaveDomainUrl = () => {
        let subdomain = formik.values.kycDomainUrl.trim();

        if (!subdomain) {
            formik.setFieldError("kycDomainUrl", "Please enter a subdomain");
            return;
        }

        subdomain = subdomain
            .replace(/^https?:\/\//, "")
            .replace(`.${baseDomain}`, "")
            .split(".")[0];

        const fullDomain = `${subdomain}.${baseDomain}`;
        formik.setFieldValue("kycDomainUrl", fullDomain);
        formik.setFieldValue("kycIsVerified", false);

        setIsManuallyEdited(true);
        setIsEditing(false);
        setButtonColor("");
        setIsButtonDisabled(false);
    };

    useEffect(() => {
        if (!formik.values.kycIsVerified) {
            formik.setFieldError("kycIsVerified", "Please verify your domain");
        }
    }, [formik.values.kycIsVerified]);

    const verifyDomainURL = async () => {
        try {
            const kycDomain = cleanDomain(formik.values.kycDomainUrl);
            const paycentralDomainValue = cleanDomain(paycentralDomain?.values?.domainUrl);
            const qbotDomainValue = cleanDomain(qbotDomain?.values?.domainUrl);

            if (
                kycDomain === paycentralDomainValue ||
                kycDomain === qbotDomainValue
            ) {
                setButtonColor("error");
                setResponseMessage(
                    "KYC domain must be different from QBOT and PayCentral domains."
                );
                formik.setFieldValue("kycIsVerified", false);
                formik.setFieldError("kycIsVerified", "Please verify your domain");
                return;
            }

            let subdomain = formik.values.kycDomainUrl?.trim();

            if (!subdomain) {
                formik.setFieldValue("kycIsVerified", false);
                formik.setFieldError("kycIsVerified", "Please verify your domain");
                setResponseMessage("Please enter a domain");
                setButtonColor("error");
                return;
            }
            subdomain = subdomain
                .replace(/^https?:\/\//, "")
                .replace(`.${baseDomain}`, "")
                .split(".")[0];

            const fullDomain = `${subdomain}.${baseDomain}`;

            const response = await verifyDomain({
                domainUrl: fullDomain,
            });

            if (response?.data?.status === 200) {
                setButtonColor("error");
                setResponseMessage("Domain already exists");
                formik.setFieldValue("kycIsVerified", false);
                formik.setFieldError("kycIsVerified", "Please verify your domain");
                setIsButtonDisabled(false);
            } else if (response?.data?.status === 202) {
                setButtonColor("success");
                setResponseMessage("Domain verified successfully");
                formik.setFieldValue("kycIsVerified", true);
                setIsButtonDisabled(true);

                formik.setFieldValue("kycDomainUrl", fullDomain);
            }

            setTimeout(() => setResponseMessage(""), 3000);
        } catch (error) {
            setButtonColor("error");
            setResponseMessage(
                error?.response?.data?.message || "Verification failed. Try again."
            );
            formik.setFieldValue("kycIsVerified", false);
            formik.setFieldError("kycIsVerified", "Please verify your domain");
            setIsButtonDisabled(false);
        }
    };

    const handleCancelEdit = () => {
        formik.setFieldValue("kycDomainUrl", previousDomain);
        formik.setFieldError("kycDomainUrl", "");
        setIsEditing(false);
    };

    const handleCompanyNameChange = (e) => {
        formik.setFieldValue(e.target.name, e.target.value.replace(/^\s+/, ''));
        formik.setFieldTouched(e.target.name, true, false);
    };

    const handleEmailChange = (e) => {
        formik.setFieldValue(e.target.name, e.target.value.replace(/\s/g, ''));
        formik.setFieldTouched(e.target.name, true, false);
    };

    useEffect(() => {
        if (formik.values.kycIsVerified) {
            setButtonColor("success");
            setIsButtonDisabled(true);
        }
    }, [formik.values.kycIsVerified]);

    useEffect(() => {
        if (formik.values.kycIsVerified) return;
        if (!isEditing && !formik.values.kycIsVerified && !isManuallyEdited && !formik.touched.kycDomainUrl && (!tenantId || isTrialPortalCreation)) {
            const username = formik.values.kycUserName;

            if (username) {
                const cleanValue = username
                    .replace(/\s+/g, "")
                    .replace(/[^a-zA-Z0-9]/g, "")
                    .toLowerCase();

                formik.setFieldValue("kycDomainUrl", `${cleanValue}.${baseDomain}`);
                formik.setFieldValue("kycIsVerified", false);
                formik.setFieldError("kycIsVerified", "Please verify your domain");
                setButtonColor("");
                setIsButtonDisabled(false);
            }
        }
    }, [formik.values.kycUserName, isEditing, formik.values.kycIsVerified, isManuallyEdited]);

    useEffect(() => {
        const kycDomain = cleanDomain(formik.values.kycDomainUrl);
        const paycentralDomainValue = cleanDomain(paycentralDomain?.values?.domainUrl);
        const qbotDomainValue = cleanDomain(qbotDomain?.values?.domainUrl);

        if (!kycDomain) return;

        if (kycDomain === paycentralDomainValue) {
            formik.setFieldError(
                "kycDomainUrl",
                "KYC and PayCentral domains cannot be the same."
            );
            return;
        }

        if (kycDomain === qbotDomainValue) {
            formik.setFieldError(
                "kycDomainUrl",
                "KYC and QBOT domains cannot be the same."
            );
            return;
        }

    }, [
        formik.values.kycDomainUrl,
        formik.values.domainUrl,
        qbotDomain?.values?.domainUrl
    ]);

    return (
        <div className="kyc-step">
            <section className="kyc-step-section">
                <div className="kyc-step-section-head">
                    <h3>KYC identity</h3>
                    <p>User name, countries, company profile, and domain.</p>
                </div>
                <div className="kyc-step-grid">
                    <div className="kyc-step-field">
                        <label htmlFor="kyc-userName">
                            User Name<span className="kyc-step-req">*</span>
                        </label>
                        <TextField
                            id="kyc-userName"
                            fullWidth
                            name="kycUserName"
                            value={formik.values.kycUserName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Enter user name"
                            error={formik.touched.kycUserName && Boolean(formik.errors.kycUserName)}
                            helperText={formik.touched.kycUserName && formik.errors.kycUserName}
                            sx={stepperTextFieldSx}
                        />
                    </div>

                    <div className="kyc-step-field">
                        <label>
                            Country<span className="kyc-step-req">*</span>
                        </label>
                        <Select
                            multiple
                            fullWidth
                            name="kycCountry"
                            value={
                                formik.values.kycCountry
                                    ? formik.values.kycCountry.split(",")
                                    : []
                            }
                            onChange={(e) => {
                                let value = e.target.value;
                                if (!Array.isArray(value)) value = [value];
                                formik.setFieldValue("kycCountry", value.join(","));
                            }}
                            onBlur={formik.handleBlur}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected.length) {
                                    return <span className="kyc-step-placeholder">Select countries</span>;
                                }

                                return (
                                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                        {selected.map((code) => {
                                            const country = countryList.find(c => c.code === code);
                                            return (
                                                <Chip
                                                    key={code}
                                                    className="kyc-step-chip"
                                                    label={country?.name || code}
                                                    size="small"
                                                />
                                            );
                                        })}
                                    </Box>
                                );
                            }}
                            sx={{
                                ...stepperTextFieldSx["& .MuiOutlinedInput-root"],
                                height: "auto",
                                minHeight: "44px",
                            }}
                        >
                            {countryList.map((country) => (
                                <MenuItem key={country.id} value={country.code}>
                                    <Checkbox
                                        checked={
                                            formik.values.kycCountry
                                                ?.split(",")
                                                ?.includes(country.code)
                                        }
                                    />
                                    <ListItemText primary={country.name} />
                                </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.kycCountry && formik.errors.kycCountry && (
                            <span className="kyc-step-msg is-error">{formik.errors.kycCountry}</span>
                        )}
                    </div>

                    <div className="kyc-step-field">
                        <label htmlFor="kyc-companyName">
                            Company Name<span className="kyc-step-req">*</span>
                        </label>
                        <TextField
                            id="kyc-companyName"
                            fullWidth
                            name="kycCompanyName"
                            value={formik.values.kycCompanyName}
                            onChange={handleCompanyNameChange}
                            onBlur={(e) => {
                                formik.setFieldValue("kycCompanyName", e.target.value.trim());
                                formik.handleBlur(e);
                            }}
                            placeholder="Enter company name"
                            disabled={Boolean(tenantId) && !isTrialPortalCreation}
                            error={formik.touched.kycCompanyName && Boolean(formik.errors.kycCompanyName)}
                            helperText={formik.touched.kycCompanyName && formik.errors.kycCompanyName}
                            sx={stepperTextFieldSx}
                        />
                    </div>

                    <div className="kyc-step-field">
                        <label htmlFor="kyc-companyEmail">
                            Company Email<span className="kyc-step-req">*</span>
                        </label>
                        <TextField
                            id="kyc-companyEmail"
                            fullWidth
                            name="kycCompanyEmail"
                            value={formik.values.kycCompanyEmail}
                            onChange={handleEmailChange}
                            onBlur={formik.handleBlur}
                            placeholder="Enter company email"
                            error={formik.touched.kycCompanyEmail && Boolean(formik.errors.kycCompanyEmail)}
                            helperText={formik.touched.kycCompanyEmail && formik.errors.kycCompanyEmail}
                            sx={stepperTextFieldSx}
                        />
                    </div>

                    <div className="kyc-step-field is-full">
                        <label htmlFor="kyc-domainUrl">
                            Domain<span className="kyc-step-req">*</span>
                        </label>
                        <div className="kyc-step-domain">
                            <div className="kyc-step-domain-input">
                                <TextField
                                    id="kyc-domainUrl"
                                    fullWidth
                                    name="kycDomainUrl"
                                    value={
                                        isEditing
                                            ? formik.values.kycDomainUrl?.split(".")[0] || ""
                                            : formik.values.kycDomainUrl || ""
                                    }
                                    onChange={(e) => {
                                        if (isEditing) {
                                            const value = e.target.value
                                                .toLowerCase()
                                                .replace(/[^a-z0-9-]/g, "");
                                            formik.setFieldValue("kycDomainUrl", value);
                                        }
                                    }}
                                    onBlur={formik.handleBlur}
                                    placeholder={isEditing ? "Enter subdomain" : ""}
                                    disabled={
                                        !isEditing ||
                                        (Boolean(tenantId) && !isTrialPortalCreation && !isTrialPortalCreation)
                                    }
                                    error={formik.touched.kycDomainUrl && Boolean(formik.errors.kycDomainUrl)}
                                    helperText={formik.touched.kycDomainUrl && formik.errors.kycDomainUrl}
                                    sx={stepperTextFieldSx}
                                    InputProps={{
                                        endAdornment: isEditing && (
                                            <span className="kyc-step-domain-suffix">
                                                .{baseDomain}
                                            </span>
                                        )
                                    }}
                                />
                            </div>

                            <div className="kyc-step-domain-actions">
                                {isEditing ? (
                                    <>
                                        <Tooltip title="Save">
                                            <IconButton onClick={handleSaveDomainUrl}>
                                                <SaveIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Cancel">
                                            <IconButton
                                                className="is-danger"
                                                onClick={handleCancelEdit}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <>
                                        {(!tenantId || isTrialPortalCreation) && (
                                            <Tooltip title="Edit">
                                                <span>
                                                    <IconButton
                                                        onClick={handleEditClick}
                                                        disabled={!formik.values.kycDomainUrl || (Boolean(tenantId) && !isTrialPortalCreation && !isTrialPortalCreation)}
                                                        sx={{ "&.Mui-disabled": { opacity: 0.5 } }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}

                                        <Tooltip
                                            title={
                                                formik.values.kycIsVerified
                                                    ? "Verified"
                                                    : "Verify Domain"
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    className={
                                                        formik.values.kycIsVerified
                                                            ? "is-success"
                                                            : buttonColor === "error"
                                                                ? "is-danger"
                                                                : ""
                                                    }
                                                    onClick={verifyDomainURL}
                                                    disabled={
                                                        isButtonDisabled ||
                                                        (Boolean(tenantId) && !isTrialPortalCreation && !isTrialPortalCreation)
                                                    }
                                                    sx={{ "&.Mui-disabled": { opacity: 0.5 } }}
                                                >
                                                    <MyVerifiedIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </>
                                )}
                            </div>
                        </div>

                        {formik.touched.kycIsVerified &&
                            formik.errors.kycIsVerified &&
                            !formik.values.kycIsVerified && (
                                <span className="kyc-step-msg is-error">
                                    {formik.errors.kycIsVerified}
                                </span>
                            )}

                        {responseMessage && (
                            <span
                                className={`kyc-step-msg ${
                                    buttonColor === "success" ? "is-success" : "is-error"
                                }`}
                            >
                                {responseMessage}
                            </span>
                        )}
                    </div>
                </div>
            </section>

            <section className="kyc-step-section">
                <div className="kyc-step-section-head">
                    <h3>Plan & billing</h3>
                    <p>Subscription plan, billing cycle, payment mode, and currency.</p>
                </div>
                <div className="kyc-step-grid">
                    <div className="kyc-step-field">
                        <label>Auto Pay</label>
                        <div
                            className={`kyc-step-toggle${
                                formik.values.kycIsAutoPay ? " is-on" : ""
                            }`}
                        >
                            <div className="kyc-step-toggle-copy">
                                <strong>Enable Auto Pay</strong>
                                <span>
                                    {formik.values.kycIsAutoPay
                                        ? "Payments renew automatically"
                                        : "Manual payment collection"}
                                </span>
                            </div>
                            <CustomSwitch
                                name="kycIsAutoPay"
                                checked={formik.values.kycIsAutoPay}
                                onChange={(event) =>
                                    formik.setFieldValue("kycIsAutoPay", event.target.checked)
                                }
                            />
                        </div>
                    </div>

                    <div className="kyc-step-field">
                        <label>
                            Select Plan<span className="kyc-step-req">*</span>
                        </label>
                        <Select
                            fullWidth
                            name="kycPlanId"
                            value={formik.values.kycPlanId || ""}
                            onChange={(e) => {
                                const selectedPlanId = e.target.value;

                                formik.setFieldValue("kycPlanId", selectedPlanId);

                                const selectedPlan = planList.find(p => p.id === selectedPlanId);

                                if (selectedPlan?.selectedBillingCycle) {
                                    formik.setFieldValue("kycBillingCycle", selectedPlan.selectedBillingCycle);
                                } else {
                                    formik.setFieldValue("kycBillingCycle", "");
                                }
                            }}
                            displayEmpty
                            disabled={Boolean(planList.length === 0)}
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <span className="kyc-step-placeholder">Select a plan</span>;
                                }
                                const plan = planList.find((p) => p.id === selected);
                                return plan ? plan.name : "";
                            }}
                            sx={stepperTextFieldSx["& .MuiOutlinedInput-root"]}
                        >
                            <MenuItem value="" disabled>
                                <em>Select a plan</em>
                            </MenuItem>

                            {planList.map((plan) => (
                                <MenuItem key={`plan-${plan.id}`} value={plan.id}>
                                    {plan.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.kycPlanId && formik.errors.kycPlanId && (
                            <span className="kyc-step-msg is-error">{formik.errors.kycPlanId}</span>
                        )}
                    </div>

                    <div className="kyc-step-field">
                        <label>
                            Billing Cycle<span className="kyc-step-req">*</span>
                        </label>
                        <Select
                            fullWidth
                            name="kycBillingCycle"
                            value={formik.values.kycBillingCycle || ""}
                            onChange={formik.handleChange}
                            displayEmpty
                            disabled={!formik.values.kycPlanId}
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <span className="kyc-step-placeholder">Select billing cycle</span>;
                                }
                                return selected;
                            }}
                            sx={stepperTextFieldSx["& .MuiOutlinedInput-root"]}
                        >
                            <MenuItem value="" disabled>
                                <em>Select billing cycle</em>
                            </MenuItem>

                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="yearly">Yearly</MenuItem>
                        </Select>
                        {formik.touched.kycBillingCycle && formik.errors.kycBillingCycle && (
                            <span className="kyc-step-msg is-error">{formik.errors.kycBillingCycle}</span>
                        )}
                    </div>

                    <div className="kyc-step-field">
                        <label htmlFor="kyc-currency">
                            Currency<span className="kyc-step-req">*</span>
                        </label>
                        <TextField
                            id="kyc-currency"
                            sx={stepperTextFieldSx}
                            select
                            name="kycCurrency"
                            size="small"
                            placeholder="Select Currency"
                            value={formik.values.kycCurrency}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.kycCurrency && Boolean(formik.errors.kycCurrency)}
                            helperText={formik.touched.kycCurrency && formik.errors.kycCurrency}
                            fullWidth
                        >
                            {tenantCurrencies.map((kycCurrency) => (
                                <MenuItem key={kycCurrency.id} value={kycCurrency.id}>
                                    {kycCurrency?.name || ""} - {kycCurrency?.symbol || ""}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>

                    <div className="kyc-step-field is-full">
                        <label>
                            Payment Mode<span className="kyc-step-req">*</span>
                        </label>
                        <div className="kyc-step-pay-grid">
                            <button
                                type="button"
                                className={`kyc-step-pay-card${
                                    formik.values.kycPaymentMode === "online" ? " is-on" : ""
                                }`}
                                onClick={() => formik.setFieldValue("kycPaymentMode", "online")}
                            >
                                <MyCreditCardIcon />
                                <strong>Online Payment</strong>
                            </button>

                            <button
                                type="button"
                                className={`kyc-step-pay-card is-offline${
                                    formik.values.kycPaymentMode === "offline" ? " is-on" : ""
                                }`}
                                onClick={() => formik.setFieldValue("kycPaymentMode", "offline")}
                            >
                                <MyAccountBalanceIcon />
                                <strong>Offline Payment</strong>
                            </button>
                        </div>
                        {formik.touched.kycPaymentMode && formik.errors.kycPaymentMode && (
                            <span className="kyc-step-msg is-error">
                                {formik.errors.kycPaymentMode}
                            </span>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default KycInfoStep;